import { isNil, map, uniqBy, orderBy } from 'lodash';

import {
  getStageMessage,
  ProgressStepOptions,
  ProfileProgressStep,
  SOFT_HALF_STOP_MESSAGES,
  ADMIN_SOFT_STOP_MESSAGES,
  MEMBER_SOFT_STOP_MESSAGES,
  ADMIN_HARD_SOFT_STOP_MESSAGES,
  MEMBER_HARD_SOFT_STOP_MESSAGES,
  arePreviousStagesCompleted,
  ProgressStepConfigParams,
  shouldDisplayRequestButton,
  shouldDisplayResponseButton,
  isSoftHardStopStage,
  getProgressStepStatus,
} from './generalProgressUtils';
import {
  BOUsersStageModel,
  StageStatusEnum,
  BOUserStatusEnum,
} from 'api/backofficeApiClient';
import { UserRole } from 'enums';
import ProfileUncompletedMessage from 'features/account/MemberProgressBar/ProfileUncompletedMessage';

type MemberProgressStep = ProfileProgressStep<BOUserStatusEnum>;
type MemberProgressStepConfig = Partial<
  Record<
    BOUserStatusEnum,
    (step: ProgressStepConfigParams) => ProgressStepOptions
  >
>;

export const getMemberProgressTrackerStageName = (
  stage: BOUserStatusEnum,
  isAdmin?: boolean,
) => {
  switch (stage) {
    case BOUserStatusEnum.Reviewed:
      return 'Profile is reviewed by MBP representative';
    case BOUserStatusEnum.OutreachConducted:
      return 'Initial outreach conducted';
    case BOUserStatusEnum.PriceAgreementSent:
      return 'Pricing and service agreement sent';
    case BOUserStatusEnum.PriceAgreementSigned:
      return 'Pricing and service agreement signed';
    case BOUserStatusEnum.Completed:
      return isAdmin ? 'Profile data is completed' : 'Profile completed';
  }
};

const MEMBER_PROFILE_STAGES = (isRequest: boolean) =>
  [
    BOUserStatusEnum.Reviewed,
    BOUserStatusEnum.OutreachConducted,
    BOUserStatusEnum.PriceAgreementSent,
    BOUserStatusEnum.PriceAgreementSigned,
    // Omit step on member request details page
    isRequest ? undefined : BOUserStatusEnum.Completed,
  ].filter(Boolean) as Array<BOUserStatusEnum>;

const ADMIN_MEMBER_PROGRESS_STEPS_CONFIG = {
  [BOUserStatusEnum.Reviewed]: () => ({
    title: getMemberProgressTrackerStageName(BOUserStatusEnum.Reviewed, true),
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [BOUserStatusEnum.OutreachConducted]: () => ({
    title: getMemberProgressTrackerStageName(
      BOUserStatusEnum.OutreachConducted,
      true,
    ),
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [BOUserStatusEnum.PriceAgreementSent]: ({
    arePrevStagesCompleted,
  }: ProgressStepConfigParams) => ({
    title: getMemberProgressTrackerStageName(
      BOUserStatusEnum.PriceAgreementSent,
      true,
    ),
    actionBtnText: 'Send',
    isActionEnabled: arePrevStagesCompleted,
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      stageCompletedMessage: 'Agreement sent',
      pendingMessage: 'Send pricing agreement',
    },
  }),
  [BOUserStatusEnum.PriceAgreementSigned]: () => ({
    title: getMemberProgressTrackerStageName(
      BOUserStatusEnum.PriceAgreementSigned,
      true,
    ),
    errorText: 'Waiting for members respond',
    isActionEnabled: false,
    responsibleRole: UserRole.Users,
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE,
      stageCompletedMessage: 'Agreement signed',
    },
  }),
  [BOUserStatusEnum.Completed]: () => ({
    title: getMemberProgressTrackerStageName(BOUserStatusEnum.Completed, true),
    errorText:
      'You can update the data now or wait till the member completes it on their own',
    isActionEnabled: false,
    responsibleRole: UserRole.Users,
    messages: {
      softStopMessage: ADMIN_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
      hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE,
    },
  }),
};

const MEMBER_PPROFILE_STAGES_CONFIG = (
  unfilledSections: React.ReactNode[],
) => ({
  [BOUserStatusEnum.Reviewed]: () => ({
    title: getMemberProgressTrackerStageName(BOUserStatusEnum.Reviewed),
    messages: {
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [BOUserStatusEnum.OutreachConducted]: () => ({
    title: getMemberProgressTrackerStageName(
      BOUserStatusEnum.OutreachConducted,
    ),
    messages: {
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [BOUserStatusEnum.PriceAgreementSent]: () => ({
    title: getMemberProgressTrackerStageName(
      BOUserStatusEnum.PriceAgreementSent,
    ),
    messages: {
      stageCompletedMessage: 'Agreement sent',
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
    },
  }),
  [BOUserStatusEnum.PriceAgreementSigned]: () => ({
    title: getMemberProgressTrackerStageName(
      BOUserStatusEnum.PriceAgreementSigned,
    ),
    errorText: 'Please sign service agreement in your profile',
    responsibleRole: UserRole.Users,
    messages: {
      stageCompletedMessage: 'Agreement signed',
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE,
    },
  }),
  [BOUserStatusEnum.Completed]: () => ({
    title: getMemberProgressTrackerStageName(BOUserStatusEnum.Completed),
    errorText: ProfileUncompletedMessage(unfilledSections),
    responsibleRole: UserRole.Users,
    messages: {
      softStopMessage: MEMBER_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE,
      halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
      hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE,
    },
  }),
});

// It's necessary for BE to send such stage, but it's not needed for Profile Progress Stages on FE
const removeUnusedStagesForProfileProgress = (stages: BOUsersStageModel[]) => {
  return stages.filter(
    ({ stage }) => !isNil(stage) && stage !== BOUserStatusEnum.Pending,
  );
};

interface MemberProgressStagesOptions {
  isAdmin?: boolean;
  stages?: BOUsersStageModel[];
  isRequest: boolean;
  unfilledSections?: React.ReactNode[];
}
const getMemberProgressSteps = (options: MemberProgressStagesOptions) => {
  const { isAdmin, unfilledSections } = options;

  return isAdmin
    ? ADMIN_MEMBER_PROGRESS_STEPS_CONFIG
    : MEMBER_PPROFILE_STAGES_CONFIG(unfilledSections as React.ReactNode[]);
};

export const getMemberProgressStages = (
  stagesOptions: MemberProgressStagesOptions,
) => {
  const { isAdmin, stages, isRequest } = stagesOptions;
  const memberStages = stages?.length
    ? removeUnusedStagesForProfileProgress(stages)
    : [];
  const memberStagesConfigByRole = getMemberProgressSteps(
    stagesOptions,
  ) as MemberProgressStepConfig;
  const memberProfileStages = MEMBER_PROFILE_STAGES(isRequest);

  const mergedProfileStages = map(
    [...memberStages, ...memberProfileStages],
    (stage) => {
      return typeof stage === 'number'
        ? {
            stage,
            stageStatus: StageStatusEnum.Pending,
          }
        : stage;
    },
  );

  const sortedProfileStages = orderBy(
    mergedProfileStages,
    [(step) => step.stage, (step) => step.stageStatus],
    ['asc', 'desc'],
  );

  const uniqProfileStages = uniqBy(
    sortedProfileStages,
    (stageConfig) => stageConfig.stage,
  );

  const profileStagesConfigs = uniqProfileStages
    .map(
      (
        {
          additionalInfoReply,
          additionalInfoRequest,
          showPulsatingIndicator,
          ...step
        },
        stepIndex,
        profileStages,
      ) => {
        const getStageConfig =
          memberStagesConfigByRole[step.stage as BOUserStatusEnum];

        if (!getStageConfig) return undefined;

        const arePrevStagesCompleted = arePreviousStagesCompleted(
          profileStages,
          step,
        );

        const stageConfig = getStageConfig({
          arePrevStagesCompleted,
        });

        const {
          messages,
          responsibleRole,
          isActionEnabled: isActionEnabledFromConfig,
          displayRequestButton: displayRequestButtonFromConfig,
          displayResponseButton: displayResponseButtonFromConfig,
          ...rest
        } = stageConfig;

        const enableRequestButton = shouldDisplayRequestButton({
          isAdmin,
          arePrevStagesCompleted,
          responsibleRole,
          stageStatus: step.stageStatus,
        });

        const enableResponseButton = shouldDisplayResponseButton({
          isAdmin,
          requestMessage: additionalInfoRequest,
          responseMessage: additionalInfoReply,
        });

        const stepValidationStatus = getProgressStepStatus({
          step,
          stepIndex,
          profileStages,
        });

        const isPulsatingStage =
          isSoftHardStopStage(step.stageStatus) &&
          !isAdmin &&
          showPulsatingIndicator;

        return {
          ...rest,
          ...step,
          responsibleRole: responsibleRole || UserRole.Admin,
          message: getStageMessage(step.stageStatus as StageStatusEnum, {
            ...messages,
            isAdmin,
            arePrevStagesCompleted,
          }),
          isPulsating: isPulsatingStage,
          requestMessage: additionalInfoRequest,
          responseMessage: additionalInfoReply,
          displayResponseButton: isNil(displayResponseButtonFromConfig)
            ? enableResponseButton
            : displayResponseButtonFromConfig,
          displayRequestButton: isNil(displayRequestButtonFromConfig)
            ? enableRequestButton
            : displayRequestButtonFromConfig,
          isActionEnabled: isNil(isActionEnabledFromConfig)
            ? isAdmin
            : isActionEnabledFromConfig,
          stepValidationStatus,
        };
      },
    )
    .filter(Boolean) as MemberProgressStep[];

  return profileStagesConfigs;
};
